import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { api, photoUrl, formatApiErrorDetail } from "@/api";
import { useAuth } from "@/context/AuthContext";
import { useRealtime } from "@/hooks/useRealtime";
import { PriorityBadge, StatusBadge } from "@/components/Badges";
import { PRIORITIES, ROLE_LABELS, fmtTime, timeAgo } from "@/lib/constants";
import { ArrowLeft, Building2, Truck, Send, ImagePlus, Lock, LockOpen, CheckCircle2, Loader2, FileBarChart, MapPin, X, ExternalLink, AlertTriangle, BookOpen, ChevronRight, Pin, Ban, Pencil, Search, FileText } from "lucide-react";
import { VoiceRecorder } from "@/components/VoiceRecorder";
import { BuildingInfo } from "@/components/BuildingInfo";
import { LiveViewer } from "@/components/LiveViewer";
import { IncidentMiniMap, IncidentDrawEditor, ApproachRouteEditor } from "@/components/IncidentMaps";
import { WeatherWidget } from "@/components/WeatherWidget";
import { Lightbox } from "@/components/Lightbox";
import { useProcedures } from "@/context/ProceduresContext";
import { toast } from "sonner";

import { PhoneCall } from "lucide-react";

function CallAssistPanel({ incidentId, typeId, pinnedIds, onPin, onSearchKb, onSubcategory, onNoteAdded }) {
  const [enabled, setEnabled] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [kbMap, setKbMap] = useState({});
  const [answers, setAnswers] = useState({});
  const [kbSug, setKbSug] = useState({});
  const [sel, setSel] = useState("");
  const [text, setText] = useState("");
  const [currentId, setCurrentId] = useState(null);
  const [open, setOpen] = useState(true);
  const [subcategories, setSubcategories] = useState([]);
  const [path, setPath] = useState([]);
  const [phase, setPhase] = useState("questions");
  useEffect(() => {
    api.get("/settings/callassist").then(({ data }) => setEnabled(!!data.enabled)).catch(() => {});
    api.get("/kb/articles").then(({ data }) => { const m = {}; (data || []).forEach((a) => { m[a.id] = a.title; }); setKbMap(m); }).catch(() => {});
  }, []);
  useEffect(() => {
    if (!typeId) return;
    api.get(`/callassist/flow/${typeId}`).then(({ data }) => {
      const qs = data.questions || []; const subs = data.subcategories || [];
      setQuestions(qs); setSubcategories(subs); setPath([]); setAnswers({}); setKbSug({});
      if (subs.length > 0) { setPhase("select"); setCurrentId(null); }
      else { setPhase("questions"); setCurrentId(qs[0]?.id || null); }
    }).catch(() => {});
  }, [typeId]);
  if (!enabled || (questions.length === 0 && subcategories.length === 0)) return null;
  const flowQs = [...questions, ...path.flatMap((n) => n.questions || [])];
  const currentChildren = path.length ? (path[path.length - 1].subcategories || []) : subcategories;
  const startQuestions = (np) => { const p = np || path; const label = p.map((n) => n.name).filter(Boolean).join(" — "); if (onSubcategory) onSubcategory(label); const aq = [...questions, ...p.flatMap((n) => n.questions || [])]; setPhase("questions"); setCurrentId(aq[0]?.id || null); };
  const pickSub = (node) => { const np = [...path, node]; setPath(np); if (!(node.subcategories && node.subcategories.length)) startQuestions(np); };
  const idx = flowQs.findIndex((q) => q.id === currentId);
  const cur = idx >= 0 ? flowQs[idx] : null;
  const nextLinear = () => (idx >= 0 && idx + 1 < flowQs.length ? flowQs[idx + 1].id : null);
  const post = async (q, answer, procId, useExtra = true) => {
    const extra = useExtra ? text.trim() : "";
    const full = extra ? `${answer} — ${extra}` : answer;
    let created;
    try { const { data } = await api.post(`/incidents/${incidentId}/notes`, { text: full }); created = data; }
    catch { toast.error("Kon niet in kladblok opslaan"); return false; }
    if (onNoteAdded && created?.id) onNoteAdded(created);
    setAnswers((a) => ({ ...a, [q.id]: { answer: full, procId: procId || null } }));
    const term = (extra || answer || "").trim();
    if (term.length >= 2) {
      api.get(`/kb/articles?q=${encodeURIComponent(term)}&scope=keyword`).then(({ data }) => setKbSug((s) => ({ ...s, [q.id]: (data || []).slice(0, 4) }))).catch(() => {});
    }
    setText("");
    setSel("");
    return true;
  };
  const answerChoice = async (q, o) => {
    if (!(await post(q, o.label, o.procedure_kb_id))) return;
    setCurrentId(o.next === "end" ? null : (o.next ? o.next : nextLinear()));
  };
  const answerText = async (q) => {
    if (!text.trim()) { toast.error("Vul een antwoord in"); return; }
    if (!(await post(q, text.trim(), q.procedure_kb_id, false))) return;
    setCurrentId(nextLinear());
  };
  const answeredList = flowQs.filter((q) => answers[q.id]);

  if (!open) return (
    <div className="mb-5"><button data-testid="callassist-reopen" onClick={() => setOpen(true)} className="inline-flex items-center gap-2 rounded-xl border border-indigo-200 bg-indigo-50 px-4 py-2 text-sm font-semibold text-indigo-700"><PhoneCall className="h-4 w-4" /> CallAssist openen</button></div>
  );
  return (
    <div className="mb-5 rounded-2xl border border-indigo-200 bg-indigo-50/60 p-5 shadow-sm" data-testid="callassist-panel">
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm font-bold text-indigo-800"><PhoneCall className="h-4 w-4" /> CallAssist — vragenflow</div>
        <button data-testid="callassist-close" onClick={() => setOpen(false)} className="rounded-lg p-1 text-indigo-400 hover:bg-indigo-100"><X className="h-4 w-4" /></button>
      </div>
      {answeredList.length > 0 && (
        <div className="mb-3 space-y-1">
          {answeredList.map((q) => (
            <div key={q.id} className="rounded-lg bg-white/70 px-3 py-1.5 text-xs">
              <div className="flex items-center gap-2">
                <span className="font-semibold text-slate-600">{q.text}:</span>
                <span className="text-slate-800">{answers[q.id].answer}</span>
                <button data-testid={`ca-kbsearch-${q.id}`} onClick={() => onSearchKb(answers[q.id].answer)} title="Zoek dit antwoord in de kennisbank"
                  className="ml-auto inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-semibold text-indigo-600 hover:bg-indigo-100">
                  <Search className="h-3 w-3" /> Zoek
                </button>
                {answers[q.id].procId && kbMap[answers[q.id].procId] && (
                  <button data-testid={`ca-proc-${q.id}`} onClick={() => onPin(answers[q.id].procId)}
                    className={`rounded px-2 py-0.5 text-[10px] font-bold text-white ${pinnedIds.includes(answers[q.id].procId) ? "bg-emerald-600" : "bg-amber-500 animate-pulse"}`}>
                    {pinnedIds.includes(answers[q.id].procId) ? "Gedeeld ✓" : `Deel: ${kbMap[answers[q.id].procId]}`}
                  </button>
                )}
              </div>
              {(kbSug[q.id] || []).length > 0 && (
                <div className="mt-1 flex flex-wrap items-center gap-1">
                  <span className="text-[10px] font-semibold uppercase tracking-wide text-indigo-400">Suggesties:</span>
                  {kbSug[q.id].map((a) => (
                    <button key={a.id} data-testid={`ca-sug-${q.id}-${a.id}`} onClick={() => onPin(a.id)} title={pinnedIds.includes(a.id) ? "Gedeeld met voertuig" : "Deel met voertuig"}
                      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold transition-colors ${pinnedIds.includes(a.id) ? "bg-emerald-600 text-white" : "border border-indigo-200 bg-white text-indigo-700 hover:bg-indigo-50"}`}>
                      <BookOpen className="h-2.5 w-2.5" /> {pinnedIds.includes(a.id) ? `${a.title} ✓` : a.title}{(a.attachments || []).length > 0 && <FileText data-testid={`ca-sug-pdf-${a.id}`} className="h-3 w-3 text-red-500" title="Bevat PDF-bijlage" />}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      {phase === "select" ? (
        <div data-testid="ca-subcat-picker" className="rounded-xl border border-indigo-100 bg-white p-3">
          {path.length > 0 && (
            <div className="mb-2 flex flex-wrap items-center gap-1 text-xs">
              <button data-testid="ca-subcat-reset" onClick={() => setPath([])} className="rounded bg-slate-100 px-2 py-0.5 font-semibold text-slate-500 hover:bg-slate-200">Type</button>
              {path.map((n) => <span key={n.id} className="inline-flex items-center gap-1"><ChevronRight className="h-3 w-3 text-slate-400" /><span className="rounded bg-indigo-100 px-2 py-0.5 font-semibold text-indigo-700">{n.name}</span></span>)}
            </div>
          )}
          <div className="mb-2 text-sm font-semibold text-slate-800">{path.length ? "Verder verfijnen?" : "Kies een subcategorie"}</div>
          <div className="flex flex-wrap gap-1.5">
            {currentChildren.map((node) => (
              <button key={node.id} data-testid={`ca-pick-${node.id}`} onClick={() => pickSub(node)}
                className="rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-1.5 text-xs font-semibold text-indigo-700 hover:bg-indigo-100">{node.name || "?"}{node.subcategories && node.subcategories.length ? " ›" : ""}</button>
            ))}
            {currentChildren.length === 0 && <span className="text-xs text-slate-400">Geen verdere niveaus.</span>}
          </div>
          <button data-testid="ca-subcat-start" onClick={() => startQuestions()} className="mt-3 rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-bold text-white">Vragen starten{path.length ? " hier" : (questions.length ? " (alleen algemeen)" : "")}</button>
        </div>
      ) : cur ? (
        <div data-testid={`ca-q-${cur.id}`} className="rounded-xl border border-indigo-100 bg-white p-3">
          {path.length > 0 && (
            <div className="mb-2 flex flex-wrap items-center gap-1 text-[11px]">
              {path.map((n) => <span key={n.id} className="rounded bg-indigo-100 px-2 py-0.5 font-semibold text-indigo-700">{n.name}</span>)}
              <button data-testid="ca-subcat-change2" onClick={() => { setPhase("select"); setPath([]); }} className="text-indigo-500 underline">wijzig</button>
            </div>
          )}
          <div className="mb-2 text-sm font-semibold text-slate-800">{cur.text}</div>
          {cur.kind === "choice" && (
            <div className="mb-2 flex flex-wrap gap-1.5">
              {(cur.options || []).map((o, i) => (
                <button key={i} data-testid={`ca-opt-${cur.id}-${i}`} onClick={() => answerChoice(cur, o)}
                  className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-indigo-50">{o.label}</button>
              ))}
            </div>
          )}
          <textarea data-testid={`ca-text-${cur.id}`} value={text} onChange={(e) => setText(e.target.value)} rows={2}
            onSelect={(e) => setSel((e.target.value.substring(e.target.selectionStart, e.target.selectionEnd) || "").trim())}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); if (cur.kind !== "choice") answerText(cur); } }}
            placeholder={cur.kind === "choice" ? "Extra info (optioneel) — Shift+Enter voor nieuwe regel" : "Antwoord van de beller… Enter = verzenden, Shift+Enter = nieuwe regel"}
            className="w-full rounded-lg border border-slate-200 px-2.5 py-1.5 text-sm outline-none focus:border-indigo-500" />
          <div className="mt-2 flex flex-wrap items-center gap-2">
            {cur.kind !== "choice" && <button data-testid={`ca-save-${cur.id}`} onClick={() => answerText(cur)} className="rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-bold text-white">Vastleggen</button>}
            {sel.length >= 2 && (
              <button type="button" data-testid="ca-kbsearch-sel" onClick={() => onSearchKb(sel)}
                className="inline-flex items-center gap-1 rounded-lg bg-yellow-400 px-3 py-1.5 text-xs font-bold text-slate-900 hover:bg-yellow-300">
                <Search className="h-3.5 w-3.5" /> Zoek "{sel.length > 14 ? sel.slice(0, 14) + "…" : sel}"
              </button>
            )}
            <button data-testid={`ca-skip-${cur.id}`} onClick={() => setCurrentId(nextLinear())} className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-500 hover:bg-slate-50">Overslaan</button>
          </div>
        </div>
      ) : (
        <div className="flex items-center justify-between gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm font-semibold text-emerald-700">
          Vragenflow afgerond.
          <div className="flex gap-2">
            {subcategories.length > 0 && <button data-testid="ca-subcat-change" onClick={() => { setPhase("select"); setPath([]); }} className="rounded-lg border border-emerald-300 bg-white px-2.5 py-1 text-xs font-semibold text-emerald-700">Andere subcategorie</button>}
            <button data-testid="callassist-restart" onClick={() => setCurrentId(flowQs[0]?.id || null)} className="rounded-lg border border-emerald-300 bg-white px-2.5 py-1 text-xs font-semibold text-emerald-700">Opnieuw</button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function IncidentDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [inc, setInc] = useState(null);
  const [openIncidents, setOpenIncidents] = useState([]);
  const [notes, setNotes] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [allVehicles, setAllVehicles] = useState([]);
  const [note, setNote] = useState("");
  const [newNoteIds, setNewNoteIds] = useState(() => new Set());
  const [sel, setSel] = useState("");
  const [lightbox, setLightbox] = useState(null);
  const [drawOpen, setDrawOpen] = useState(false);
  const [savingDraw, setSavingDraw] = useState(false);
  const [approachOpen, setApproachOpen] = useState(false);
  const [approachDraft, setApproachDraft] = useState([[], []]);
  const [sharedLayer, setSharedLayer] = useState(null);
  const [legend, setLegend] = useState(null);
  const [approachEnabled, setApproachEnabled] = useState(true);
  const [autoCloseAsk, setAutoCloseAsk] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [switcherLocked, setSwitcherLocked] = useState(() => localStorage.getItem("switcherLocked") === "1");
  const toggleSwitcherLock = () => setSwitcherLocked((v) => { const nv = !v; localStorage.setItem("switcherLocked", nv ? "1" : "0"); return nv; });
  const notesEnd = useRef(null);
  const fileRef = useRef(null);
  const prevVehCount = useRef(null);
  const incRef = useRef("");
  const vehRef = useRef("");
  const openRef = useRef("");
  const notesRef = useRef("");
  const photosRef = useRef("");
  const prevNotesLen = useRef(0);

  const loadAll = useCallback(async () => {
    const [i, v, o] = await Promise.all([
      api.get(`/incidents/${id}`),
      api.get(`/vehicles`),
      api.get(`/incidents?status=open`),
    ]);
    const is = JSON.stringify(i.data); if (is !== incRef.current) { incRef.current = is; setInc(i.data); }
    const vs = JSON.stringify(v.data); if (vs !== vehRef.current) { vehRef.current = vs; setAllVehicles(v.data); }
    const os = JSON.stringify(o.data); if (os !== openRef.current) { openRef.current = os; setOpenIncidents(o.data); }
    api.get(`/incidents/${id}/notes`).then(({ data }) => { if (JSON.stringify(data) !== notesRef.current) setNotes(data); }).catch(() => {});
    api.get(`/incidents/${id}/photos`).then(({ data }) => { const ps = JSON.stringify(data); if (ps !== photosRef.current) { photosRef.current = ps; setPhotos(data); } }).catch(() => {});
  }, [id]);

  useEffect(() => { loadAll(); }, [loadAll]);
  useEffect(() => { const t = setInterval(() => { loadAll(); }, 3000); return () => clearInterval(t); }, [loadAll]);
  useRealtime((msg) => {
    if (msg.type === "__ping") return;
    if (msg.type === "map_layer_updated") { api.get("/map-layer").then(({ data }) => setSharedLayer(data.enabled !== false ? data.drawings : null)).catch(() => {}); return; }
    if (msg.type === "color_legend_updated") { api.get("/settings/color-legend").then(({ data }) => setLegend(data.legend || null)).catch(() => {}); return; }
    if (msg.type === "note_added") {
      if (msg.payload?.incident_id === id) {
        setNotes((n) => (n.some((x) => x.id === msg.payload.id) ? n : [...n, msg.payload]));
        if (msg.payload.author !== user.name) {
          const nid = msg.payload.id;
          setNewNoteIds((s) => { const x = new Set(s); x.add(nid); return x; });
          setTimeout(() => setNewNoteIds((s) => { const x = new Set(s); x.delete(nid); return x; }), 6000);
        }
      }
      return;
    }
    if (msg.type === "vehicle_updated" && msg.payload?.id) {
      const pv = msg.payload;
      const patch = (v) => (v.id === pv.id ? { ...v, status: pv.status, status_since: pv.status_since } : v);
      setAllVehicles((prev) => (prev.some((v) => v.id === pv.id && v.status !== pv.status) ? prev.map(patch) : prev));
      setInc((prev) => (prev && prev.vehicles?.some((v) => v.id === pv.id && v.status !== pv.status) ? { ...prev, vehicles: prev.vehicles.map(patch) } : prev));
    }
    if (msg.type === "__ws_open" || ["photo_added", "incident_updated", "incident_closed", "vehicle_updated", "incident_created", "incident_ack", "incident_retracted", "incident_repaged"].includes(msg.type)) loadAll();
  });
  useEffect(() => { notesRef.current = JSON.stringify(notes); }, [notes]);
  useEffect(() => {
    if (notes.length > prevNotesLen.current) {
      notesEnd.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
    prevNotesLen.current = notes.length;
  }, [notes]);
  const setSubcategory = async (label) => {
    setInc((prev) => (prev ? { ...prev, subcategory: label } : prev));
    try { await api.put(`/incidents/${id}/subcategory`, { subcategory: label }); } catch {}
  };
  useEffect(() => {
    if (inc && location.state?.openCallAssist) {
      const el = document.getElementById("callassist-anchor");
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "center" }), 300);
    }
  }, [inc, location.state]);

  const canManage = ["beheerder", "meldkamer"].includes(user.role);

  const addNote = async (e) => {
    e.preventDefault();
    if (!note.trim()) return;
    try {
      const { data } = await api.post(`/incidents/${id}/notes`, { text: note });
      setNotes((prev) => (prev.some((x) => x.id === data.id) ? prev : [...prev, data]));
      setNote("");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/incidents/${id}/photos`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Foto toegevoegd");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const toggleAssign = async (vid) => {
    const current = inc.vehicles.map((v) => v.id);
    const next = current.includes(vid) ? current.filter((x) => x !== vid) : [...current, vid];
    try {
      await api.put(`/incidents/${id}/assign`, { vehicle_ids: next });
      toast.success("Toewijzing bijgewerkt");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const [closeWizard, setCloseWizard] = useState(false);
  const [wizardStep, setWizardStep] = useState("choice");
  const [wentWell, setWentWell] = useState("");
  const [learning, setLearning] = useState("");
  const [closing, setClosing] = useState(false);

  const doClose = async (withReport) => {
    setClosing(true);
    try {
      await api.post(`/incidents/${id}/close`, { went_well: wentWell, learning_goals: learning });
      toast.success("Incident gesloten en gearchiveerd");
      if (withReport) navigate(`/incidenten/${id}/rapport`);
      else navigate("/incidenten");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setClosing(false); }
  };

  const closeIncident = () => { setWentWell(""); setLearning(""); setWizardStep("choice"); setCloseWizard(true); };

  const [retractOpen, setRetractOpen] = useState(false);
  const [retractStep, setRetractStep] = useState("choice"); // choice | askClose
  const [retracting, setRetracting] = useState(false);
  const openRetract = () => { setRetractStep("choice"); setRetractOpen(true); };
  const doRetract = async (body) => {
    setRetracting(true);
    try {
      await api.post(`/incidents/${id}/retract`, body);
      toast.success(body.scope === "all" ? "Hele melding ingetrokken" : "Voertuig ingetrokken");
      await loadAll();
      setRetractStep("askClose");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setRetracting(false); }
  };

  const { open: kbOpen, setOpen: setKbOpen, setHits, setActive, setKeyword } = useProcedures();
  const [kbResults, setKbResults] = useState([]);
  const [kbQuery, setKbQuery] = useState("");
  const [kbExpanded, setKbExpanded] = useState(null);
  const [matchedKw, setMatchedKw] = useState({});
  useEffect(() => { setActive(true); return () => { setActive(false); setHits(0); setKeyword(false); setKbOpen(false); }; }, []); // eslint-disable-line
  useEffect(() => {
    if (!inc) return;
    let cancelled = false;
    const run = async () => {
      if (kbQuery.trim()) {
        const res = await api.get(`/kb/articles?q=${encodeURIComponent(kbQuery)}`).catch(() => ({ data: [] }));
        if (!cancelled) setKbResults(res.data || []);
        return;
      }
      // Baseline: (keyword) tussen haakjes in opgeslagen kladblokregels = gerichte zoekterm; plus suggesties op titel/omschrijving/kladblok.
      const blob = (notes || []).map((n) => n.text).join(" ");
      const kws = [...new Set((blob.match(/\(([^)]+)\)/g) || []).map((m) => m.slice(1, -1).trim().toLowerCase()).filter((k) => k.length >= 2))];
      const suggestText = [inc.title, inc.description, ...(notes || []).map((n) => n.text)].filter(Boolean).join(" ");
      const [kwLists, suggest] = await Promise.all([
        Promise.all(kws.map((kw) => api.get(`/kb/articles?q=${encodeURIComponent(kw)}&scope=keyword`).then((r) => ({ kw, list: r.data })).catch(() => ({ kw, list: [] })))),
        api.post(`/kb/suggest`, { incident_type_id: inc.incident_type_id, text: suggestText, limit: 8 }).then((r) => r.data).catch(() => []),
      ]);
      if (cancelled) return;
      const map = new Map(); const mk = {};
      kwLists.forEach(({ kw, list }) => (list || []).forEach((a) => { map.set(a.id, a); (mk[a.id] = mk[a.id] || []).push(kw); }));
      (suggest || []).forEach((a) => { if (!map.has(a.id)) map.set(a.id, a); });
      const arr = [...map.values()];
      setKbResults(arr); setHits(arr.length); setKeyword(kws.length > 0); setMatchedKw(mk);
    };
    run();
    return () => { cancelled = true; };
  }, [kbQuery, inc?.id, notes?.length]); // eslint-disable-line

  const popout = (kind) => window.open(`/popout/${kind}/${id}`, `dispatchme-${kind}-${id}`, "width=1000,height=760,menubar=no,toolbar=no");

  const saveDrawings = async (gj) => {
    setSavingDraw(true);
    try { await api.put(`/incidents/${id}/drawings`, { drawings: gj }); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSavingDraw(false); }
  };

  const saveRouteAssign = async (vid, idx) => {
    const cur = { ...(inc?.route_assignments || {}) };
    if (idx == null) delete cur[vid]; else cur[vid] = idx;
    try { await api.put(`/incidents/${id}/route-assignments`, { assignments: cur }); await loadAll(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  useEffect(() => { api.get("/map-layer").then(({ data }) => setSharedLayer(data.enabled !== false ? data.drawings : null)).catch(() => {}); }, []);
  useEffect(() => { api.get("/settings/color-legend").then(({ data }) => setLegend(data.legend || null)).catch(() => {}); }, []);
  useEffect(() => { api.get("/settings/map").then(({ data }) => setApproachEnabled(data.approach_routes_enabled !== false)).catch(() => {}); }, []);

  // Auto-prompt "Melding sluiten?" zodra alle voertuigen zijn ingetrokken (overgang >0 -> 0 bij open melding).
  useEffect(() => {
    if (!inc) return;
    const n = inc.vehicles?.length || 0;
    const prev = prevVehCount.current;
    prevVehCount.current = n;
    if (prev != null && prev > 0 && n === 0 && inc.status === "open" && canManage && !retractOpen) setAutoCloseAsk(true);
  }, [inc?.vehicles?.length, inc?.status]); // eslint-disable-line

  const openApproach = () => { const base = (inc?.approach_lines && inc.approach_lines.length) ? inc.approach_lines : (inc?.building?.approach_routes && inc.building.approach_routes.length ? inc.building.approach_routes : [[], []]); setApproachDraft(base.map((r) => [...(r || [])])); setDrawOpen(false); setApproachOpen(true); };
  const saveApproach = async () => {
    try { await api.put(`/incidents/${id}/approach-lines`, { lines: approachDraft }); toast.success("Aanrijlijnen opgeslagen"); setApproachOpen(false); await loadAll(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const searchKeyword = async (term) => {
    const t = (term || "").trim();
    if (t.length < 2) return;
    try {
      const { data } = await api.get(`/kb/articles?q=${encodeURIComponent(t)}&scope=keyword`);
      setKbResults(data || []); setHits((data || []).length); setKeyword(true); setKbOpen(true);
      const mk = {}; (data || []).forEach((a) => { mk[a.id] = [t]; }); setMatchedKw(mk);
    } catch (e) { /* ignore */ }
  };

  const pinnedIds = inc?.pinned_kb_ids || [];
  const addKbTag = async (aid, tag) => {
    const t = (tag || "").trim(); if (t.length < 2) return;
    try { const { data } = await api.post(`/kb/articles/${aid}/tags`, { tag: t }); setKbResults((r) => r.map((a) => (a.id === aid ? data : a))); toast.success(`Trefwoord "${t}" toegevoegd`); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const delKbTag = async (aid, tag) => {
    try { const { data } = await api.delete(`/kb/articles/${aid}/tags/${encodeURIComponent(tag)}`); setKbResults((r) => r.map((a) => (a.id === aid ? data : a))); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const togglePin = async (aid) => {
    const next = pinnedIds.includes(aid) ? pinnedIds.filter((x) => x !== aid) : [...pinnedIds, aid];
    try {
      await api.put(`/incidents/${id}/procedures`, { kb_ids: next });
      toast.success(next.includes(aid) ? "Procedure zichtbaar in voertuig-app" : "Procedure verborgen voor voertuig");
      loadAll();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const approveDelete = async (p) => {
    try { await api.delete(`/incidents/${id}/photos/${p.id}`); toast.success("Foto verwijderd"); setLightbox(null); loadAll(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const rejectDelete = async (p) => {
    try { await api.post(`/incidents/${id}/photos/${p.id}/keep`, {}); toast.success("Verwijderverzoek afgewezen"); setLightbox(null); loadAll(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  if (!inc) return <div className="text-slate-400">Laden…</div>;
  const closed = inc.status === "closed";

  return (
    <div className="space-y-5" data-testid="incident-detail-page">
      <button data-testid="back-btn" onClick={() => navigate(-1)} className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-slate-900">
        <ArrowLeft className="h-4 w-4" /> Terug
      </button>
      {(inc.pinned_kb || []).length > 0 && (
        <div data-testid="incident-pinned-procedures" className="rounded-xl border border-blue-200 bg-blue-50 px-3 py-2 text-xs font-semibold text-blue-700">
          <BookOpen className="mr-1 inline h-3.5 w-3.5" /> {inc.pinned_kb.length} procedure(s) gedeeld met de voertuig-app
        </div>
      )}

      {/* Quick switcher: open incidents */}
      {openIncidents.length > 0 && (
        <div data-testid="incident-switcher" className={switcherLocked ? "sticky top-0 z-40" : ""}>
          <div className={switcherLocked ? "rounded-xl border border-slate-200 bg-white/95 p-2 shadow-md backdrop-blur" : ""}>
            <div className="mb-1.5 flex items-center justify-between px-1">
              <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Actieve incidenten ({openIncidents.length})</span>
              <button data-testid="switcher-lock-btn" onClick={toggleSwitcherLock}
                title={switcherLocked ? "Balk losmaken — scrollt mee weg" : "Balk vastzetten bovenaan tijdens scrollen"}
                className={`inline-flex items-center gap-1 rounded-lg border px-2 py-1 text-[11px] font-semibold transition-colors ${switcherLocked ? "border-blue-500 bg-blue-50 text-blue-600" : "border-slate-200 bg-white text-slate-500 hover:bg-slate-50"}`}>
                {switcherLocked ? <><Lock className="h-3 w-3" /> Vastgezet</> : <><LockOpen className="h-3 w-3" /> Losgemaakt</>}
              </button>
            </div>
            <div className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-1">
              {openIncidents.map((o) => {
                const active = o.id === inc.id;
                return (
                  <button
                    key={o.id}
                    data-testid={`switch-incident-${o.id}`}
                    onClick={() => navigate(`/incidenten/${o.id}`)}
                    className={`flex w-72 shrink-0 items-start gap-2.5 rounded-xl border bg-white p-3 text-left shadow-sm transition-all hover:shadow-md ${active ? "border-blue-500 ring-1 ring-blue-500" : "border-slate-200"}`}
                  >
                    <div className="mt-0.5 h-11 w-1 shrink-0 rounded-full" style={{ backgroundColor: PRIORITIES[o.priority].hex }} />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5">
                        <span className="rounded px-1.5 py-0.5 text-[11px] font-bold" style={{ backgroundColor: PRIORITIES[o.priority].bg, color: PRIORITIES[o.priority].text }}>P{o.priority}</span>
                        <span className="font-mono text-[11px] text-slate-400">{o.ref}</span>
                      </div>
                      <div className="mt-0.5 truncate text-sm font-semibold text-slate-900">{o.title}</div>
                      <div className="truncate text-xs text-slate-400">{o.building?.name}</div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="rounded-2xl border-l-4 bg-white p-5 shadow-sm" style={{ borderLeftColor: PRIORITIES[inc.priority].hex }}>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <PriorityBadge priority={inc.priority} />
              <span className="font-mono text-xs text-slate-400">{inc.ref}</span>
              {closed ? (
                <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-600"><Lock className="h-3 w-3" /> Gesloten</span>
              ) : (
                <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-semibold text-emerald-700"><span className="h-1.5 w-1.5 rounded-full bg-emerald-500 live-dot" /> Open</span>
              )}
            </div>
            <h1 className="mt-2 font-display text-2xl font-bold text-slate-900">{inc.title}</h1>
            <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-slate-500">
              <span className="inline-flex items-center gap-1"><Building2 style={{ width: 14, height: 14 }} />{inc.building?.name}</span>
              <span>{inc.incident_type?.name}</span>
              {inc.subcategory && <span data-testid="incident-subcategory" className="rounded-full bg-indigo-100 px-2 py-0.5 text-xs font-bold text-indigo-700">{inc.subcategory}</span>}
            </div>
            {inc.description && <p className="mt-3 max-w-2xl text-sm text-slate-600">{inc.description}</p>}
          </div>
          {canManage && (
            <div className="flex flex-wrap gap-2">
              <button data-testid="report-btn" onClick={() => navigate(`/incidenten/${id}/rapport`)}
                className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50">
                <FileBarChart className="h-4 w-4" /> Rapport
              </button>
              {!closed && (
                <button data-testid="retract-incident-btn" onClick={openRetract}
                  className="inline-flex items-center gap-2 rounded-xl border border-amber-300 bg-amber-50 px-4 py-2.5 text-sm font-semibold text-amber-700 hover:bg-amber-100">
                  <Ban className="h-4 w-4" /> Intrekken
                </button>
              )}
              {!closed && (
                <button data-testid="close-incident-btn" onClick={closeIncident}
                  className="inline-flex items-center gap-2 rounded-xl bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-emerald-500">
                  <CheckCircle2 className="h-4 w-4" /> Sluiten
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {closeWizard && (
        <div className="fixed inset-0 z-[1500] flex items-center justify-center bg-black/50 p-4" onClick={() => !closing && setCloseWizard(false)}>
          <div onClick={(e) => e.stopPropagation()} className="w-full max-w-lg rounded-2xl bg-white p-5 shadow-2xl" data-testid="close-wizard">
            <div className="mb-3 flex items-center justify-between">
              <div className="text-sm font-bold text-slate-900">Incident sluiten</div>
              <button onClick={() => setCloseWizard(false)} disabled={closing} className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100"><X className="h-5 w-5" /></button>
            </div>
            {wizardStep === "choice" ? (
              <div className="space-y-3">
                <p className="text-sm text-slate-600">Wil je een korte evaluatie meegeven bij dit incident? Dit helpt bij het rapport en bij leerdoelen.</p>
                <div className="flex flex-col gap-2">
                  <button data-testid="wizard-yes" onClick={() => setWizardStep("feedback")} className="rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-500">Ja, evaluatie toevoegen</button>
                  <button data-testid="wizard-no" onClick={() => doClose(false)} disabled={closing} className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60">Nee, direct sluiten</button>
                </div>
                <p className="text-center text-xs text-slate-400">Rapport kun je later ook nog maken voor het archief.</p>
              </div>
            ) : (
              <div className="space-y-4">
                <div>
                  <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Wat ging goed?</label>
                  <textarea data-testid="wizard-wentwell" value={wentWell} onChange={(e) => setWentWell(e.target.value)} rows={3} className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-blue-500" placeholder="Beschrijf wat goed ging…" />
                  <div className="mt-1"><VoiceRecorder onTranscript={(t) => setWentWell((v) => (v ? v + " " : "") + t)} /></div>
                </div>
                <div>
                  <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Wat zijn de leerdoelen?</label>
                  <textarea data-testid="wizard-learning" value={learning} onChange={(e) => setLearning(e.target.value)} rows={3} className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-blue-500" placeholder="Beschrijf de leerpunten…" />
                  <div className="mt-1"><VoiceRecorder onTranscript={(t) => setLearning((v) => (v ? v + " " : "") + t)} /></div>
                </div>
                <div className="flex flex-wrap justify-end gap-2">
                  <button data-testid="wizard-close-only" onClick={() => doClose(false)} disabled={closing} className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60">Sluiten</button>
                  <button data-testid="wizard-close-report" onClick={() => doClose(true)} disabled={closing} className="inline-flex items-center gap-2 rounded-xl bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-emerald-500 disabled:opacity-60"><FileBarChart className="h-4 w-4" /> Sluiten en rapport</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {autoCloseAsk && !closed && (
        <div className="fixed inset-0 z-[1600] flex items-center justify-center bg-black/50 p-4" onClick={() => setAutoCloseAsk(false)} data-testid="autoclose-modal">
          <div onClick={(e) => e.stopPropagation()} className="w-full max-w-md rounded-2xl bg-white p-5 shadow-2xl">
            <div className="mb-3 flex items-center gap-2 text-sm font-bold text-slate-900"><AlertTriangle className="h-5 w-5 text-amber-500" /> Alle voertuigen ingetrokken</div>
            <p className="text-sm text-slate-600">Er zijn geen voertuigen meer gekoppeld aan deze melding. Wil je de melding nu sluiten?</p>
            <div className="mt-4 flex justify-end gap-2">
              <button data-testid="autoclose-keep-open" onClick={() => setAutoCloseAsk(false)} className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50">Nee, open laten</button>
              <button data-testid="autoclose-then-close" onClick={() => { setAutoCloseAsk(false); closeIncident(); }} className="inline-flex items-center gap-2 rounded-xl bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-emerald-500"><CheckCircle2 className="h-4 w-4" /> Ja, melding sluiten</button>
            </div>
          </div>
        </div>
      )}

      {retractOpen && (
        <div className="fixed inset-0 z-[1500] flex items-center justify-center bg-black/50 p-4" onClick={() => !retracting && setRetractOpen(false)}>
          <div onClick={(e) => e.stopPropagation()} className="w-full max-w-lg rounded-2xl bg-white p-5 shadow-2xl" data-testid="retract-modal">
            <div className="mb-3 flex items-center justify-between">
              <div className="text-sm font-bold text-slate-900">Melding intrekken</div>
              <button onClick={() => setRetractOpen(false)} disabled={retracting} className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100"><X className="h-5 w-5" /></button>
            </div>
            {retractStep === "choice" ? (
              <div className="space-y-3">
                <p className="text-sm text-slate-600">Trek de hele melding in (alle gekoppelde voertuigen) of annuleer één specifiek voertuig. Ingetrokken voertuigen komen weer beschikbaar en zien "Melding ingetrokken".</p>
                <button data-testid="retract-all-btn" onClick={() => doRetract({ scope: "all" })} disabled={retracting || inc.vehicles.length === 0}
                  className="flex w-full items-center justify-center gap-2 rounded-xl bg-red-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-red-500 disabled:opacity-50">
                  {retracting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Ban className="h-4 w-4" />} Hele melding intrekken
                </button>
                <div className="pt-1 text-xs font-semibold uppercase tracking-wide text-slate-500">Of één voertuig annuleren</div>
                {inc.vehicles.length === 0 ? (
                  <div className="rounded-xl border border-dashed border-slate-200 p-4 text-center text-xs text-slate-400">Geen gekoppelde voertuigen</div>
                ) : (
                  <div className="space-y-2">
                    {inc.vehicles.map((v) => (
                      <div key={v.id} className="flex items-center justify-between rounded-xl border border-slate-200 px-3 py-2">
                        <span className="font-mono text-sm font-semibold text-slate-800">{v.callsign}</span>
                        <button data-testid={`retract-veh-${v.id}`} onClick={() => doRetract({ vehicle_id: v.id })} disabled={retracting}
                          className="rounded-lg bg-amber-100 px-3 py-1 text-xs font-semibold text-amber-700 hover:bg-amber-200 disabled:opacity-50">Intrekken</button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-4" data-testid="retract-ask-close">
                <p className="text-sm text-slate-600">De intrekking is verstuurd naar de voertuigen en pagers. Wil je de melding nu sluiten?</p>
                <div className="flex justify-end gap-2">
                  <button data-testid="retract-keep-open" onClick={() => setRetractOpen(false)} className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50">Nee, open laten</button>
                  <button data-testid="retract-then-close" onClick={() => { setRetractOpen(false); closeIncident(); }} className="inline-flex items-center gap-2 rounded-xl bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-emerald-500"><CheckCircle2 className="h-4 w-4" /> Ja, melding sluiten</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <BuildingInfo building={inc.building} token={localStorage.getItem("token")} />

      {inc.tweezijdig_aanrijden && (
        <div data-testid="tweezijdig-warning" className="flex items-start gap-3 rounded-2xl border-2 border-red-300 bg-red-50 p-4">
          <AlertTriangle className="mt-0.5 h-6 w-6 shrink-0 text-red-600" />
          <div>
            <div className="text-sm font-bold uppercase tracking-wide text-red-700">Let op: tweezijdig aanrijden</div>
            <div className="text-sm text-red-600">Deze locatie/melding vereist aanrijden vanaf twee zijden. Stem de inzet en aanrijroute hierop af.</div>
          </div>
        </div>
      )}

      {(inc.building || inc.location_lat != null) && (
        <WeatherWidget lat={inc.building ? inc.building.lat : inc.location_lat} lng={inc.building ? inc.building.lng : inc.location_lng} />
      )}

      {(inc.building || inc.location_lat != null || (canManage && !closed)) && (
        <div className="grid gap-5 lg:grid-cols-2">
          {(inc.building || inc.location_lat != null) && (
            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="incident-location-panel">
              <div className="mb-3 flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 text-sm font-semibold text-slate-700"><MapPin style={{ width: 16, height: 16 }} /> Locatie {inc.building ? `· ${inc.building.name}` : (inc.location_label ? `· ${inc.location_label}` : "")}</div>
                <div className="flex items-center gap-2">
                  {canManage && !closed && approachEnabled && (
                    <button data-testid="approach-toggle-btn" onClick={() => (approachOpen ? setApproachOpen(false) : openApproach())} title="Aanrijlijnen tekenen"
                      className={`inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs font-semibold ${approachOpen ? "border-amber-500 bg-amber-500 text-white" : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"}`}>
                      <MapPin className="h-3.5 w-3.5" /> Aanrijlijnen
                    </button>
                  )}
                  {canManage && !closed && (
                    <button data-testid="draw-toggle-btn" onClick={() => { setApproachOpen(false); setDrawOpen((v) => !v); }} title="Tekenen op kaart"
                      className={`inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs font-semibold ${drawOpen ? "border-blue-500 bg-blue-600 text-white" : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"}`}>
                      <Pencil className="h-3.5 w-3.5" /> Tekenen
                    </button>
                  )}
                  <button data-testid="popout-map-btn" onClick={() => popout("kaart")} title="Open in apart venster"
                    className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50">
                    <ExternalLink className="h-3.5 w-3.5" /> Apart venster
                  </button>
                </div>
              </div>
              {approachOpen ? (
                <div className="space-y-2">
                  <ApproachRouteEditor center={{ lat: inc.building ? inc.building.lat : inc.location_lat, lng: inc.building ? inc.building.lng : inc.location_lng }} value={approachDraft} onChange={setApproachDraft} />
                  <div className="flex justify-end gap-2">
                    <button data-testid="approach-cancel" onClick={() => setApproachOpen(false)} className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50">Sluiten</button>
                    <button data-testid="approach-save" onClick={saveApproach} className="rounded-lg bg-amber-500 px-3 py-1.5 text-xs font-bold text-white hover:bg-amber-400">Opslaan</button>
                  </div>
                </div>
              ) : drawOpen ? (
                <IncidentDrawEditor
                  lat={inc.building ? inc.building.lat : inc.location_lat}
                  lng={inc.building ? inc.building.lng : inc.location_lng}
                  label={inc.building ? inc.building.name : (inc.location_label || "Locatie")}
                  initial={inc.map_drawings}
                  onSave={saveDrawings}
                  onCancel={() => { setDrawOpen(false); loadAll(); }}
                  saving={savingDraw}
                />
              ) : (
                <IncidentMiniMap
                  lat={inc.building ? inc.building.lat : inc.location_lat}
                  lng={inc.building ? inc.building.lng : inc.location_lng}
                  label={inc.building ? inc.building.name : (inc.location_label || "Locatie")}
                  routes={approachEnabled ? ((inc.approach_lines && inc.approach_lines.some((r) => r && r.length)) ? inc.approach_lines : (inc.building?.approach_routes || [])) : []}
                  tweezijdig={approachEnabled ? !!inc.tweezijdig_aanrijden : false}
                  drawings={inc.map_drawings}
                  sharedLayer={sharedLayer}
                  legend={legend}
                />
              )}
            </div>
          )}
          {canManage && !closed && <LiveViewer incidentId={id} onSaved={loadAll} />}
        </div>
      )}

      {!closed && <div id="callassist-anchor"><CallAssistPanel incidentId={id} typeId={inc.incident_type_id} pinnedIds={pinnedIds} onPin={togglePin} onSearchKb={searchKeyword} onSubcategory={setSubcategory} onNoteAdded={(n) => setNotes((prev) => (prev.some((x) => x.id === n.id) ? prev : [...prev, n]))} /></div>}

      <div className="grid gap-5 lg:grid-cols-3">
        {/* Left: vehicles + photos */}
        <div className="space-y-5">
          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="assign-panel">
            <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-700"><Truck style={{ width: 16, height: 16 }} /> Toegewezen voertuigen</div>
            <div className="space-y-2">
              {allVehicles.map((v) => {
                const assigned = inc.vehicles.some((x) => x.id === v.id);
                return (
                  <div key={v.id} className={`flex items-center justify-between rounded-xl border px-3 py-2 ${assigned ? "border-blue-200 bg-blue-50" : "border-slate-100 bg-slate-50"}`}>
                    <div className="min-w-0">
                      <div className="font-mono text-sm font-semibold text-slate-900">{v.callsign}</div>
                      <StatusBadge status={v.status} className="mt-1" />
                    </div>
                    {canManage && !closed && (
                      <button data-testid={`toggle-assign-${v.id}`} onClick={() => toggleAssign(v.id)}
                        className={`rounded-lg px-2.5 py-1 text-xs font-semibold ${assigned ? "bg-red-50 text-red-600" : "bg-blue-600 text-white"}`}>
                        {assigned ? "Ontkoppelen" : "Toewijzen"}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {canManage && !closed && approachEnabled && ((inc.building?.approach_routes || []).some((r) => r && r.length) || (inc.approach_lines || []).some((r) => r && r.length) || inc.tweezijdig_aanrijden) && inc.vehicles.length > 0 && (
            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="route-assign-panel">
              <div className="flex items-center gap-2 text-sm font-semibold text-slate-700"><MapPin style={{ width: 16, height: 16 }} /> Aanrijzijden (tweezijdig)</div>
              <div className="mb-3 mt-0.5 text-xs text-slate-400">Wijs elk voertuig een aanrijzijde toe. De crew ziet in de voertuig-app hun zijde gemarkeerd.</div>
              <div className="space-y-2">
                {inc.vehicles.map((v) => {
                  const cur = inc.route_assignments?.[v.id];
                  return (
                    <div key={v.id} className="flex items-center justify-between gap-2 rounded-xl border border-slate-100 px-3 py-2">
                      <span className="font-mono text-sm font-semibold text-slate-900">{v.callsign}</span>
                      <div className="flex gap-1.5">
                        {[0, 1].map((idx) => (
                          <button key={idx} data-testid={`route-assign-${v.id}-${idx}`} onClick={() => saveRouteAssign(v.id, cur === idx ? null : idx)}
                            className={`rounded-lg px-3 py-1.5 text-xs font-bold ${cur === idx ? "text-white" : "border border-slate-200 bg-white text-slate-600 hover:bg-slate-50"}`}
                            style={cur === idx ? { backgroundColor: idx === 0 ? "#2563eb" : "#f59e0b" } : {}}>
                            Zijde {idx + 1}
                          </button>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {(inc.acks || []).length > 0 && (
            <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-5 shadow-sm" data-testid="acks-panel">
              <div className="mb-3 flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 text-sm font-semibold text-emerald-800"><CheckCircle2 style={{ width: 16, height: 16 }} /> Bevestigd door</div>
                {inc.vehicles.length > 0 && (
                  <span data-testid="ack-count" className={`rounded-full px-2.5 py-1 text-xs font-bold tabular-nums ${inc.acks.length >= inc.vehicles.length ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>{inc.acks.length}/{inc.vehicles.length} bevestigd</span>
                )}
              </div>
              <div className="space-y-1.5">
                {inc.acks.map((a, i) => (
                  <div key={`${a.id}-${i}`} data-testid={`ack-${a.id}`} className="flex items-center justify-between rounded-lg bg-white px-3 py-2 text-sm">
                    <span className="font-semibold text-slate-800">{a.name} <span className="text-xs font-normal text-slate-400">· {ROLE_LABELS[a.role] || a.role}</span></span>
                    <span className="font-mono text-[11px] text-slate-400">{fmtTime(a.at)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="photos-panel">
            <div className="mb-3 flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm font-semibold text-slate-700"><ImagePlus style={{ width: 16, height: 16 }} /> Foto's ({photos.length})</div>
              {!closed && (
                <>
                  <input ref={fileRef} data-testid="photo-input" type="file" accept="image/*" className="hidden" onChange={upload} />
                  <button data-testid="upload-photo-btn" onClick={() => fileRef.current?.click()} disabled={uploading}
                    className="inline-flex items-center gap-1.5 rounded-lg bg-slate-900 px-2.5 py-1.5 text-xs font-semibold text-white hover:bg-slate-700 disabled:opacity-60">
                    {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ImagePlus className="h-3.5 w-3.5" />} Upload
                  </button>
                </>
              )}
            </div>
            {photos.length === 0 ? (
              <div className="rounded-xl border border-dashed border-slate-200 p-6 text-center text-xs text-slate-400">Nog geen foto's</div>
            ) : (
              <div className="grid grid-cols-3 gap-2">
                {photos.map((p, idx) => (
                  <button key={p.id} data-testid={`photo-${p.id}`} onClick={() => setLightbox(idx)} className="group relative aspect-square overflow-hidden rounded-lg bg-slate-100">
                    <img src={photoUrl(p.id)} alt={p.original_filename} className="h-full w-full object-cover transition-transform group-hover:scale-105" />
                    {p.source === "live_snapshot" && <span className="absolute left-1 top-1 rounded bg-red-600 px-1 py-0.5 text-[8px] font-bold text-white">LIVE</span>}
                    {p.delete_requested && <span data-testid={`photo-flag-${p.id}`} className="absolute right-1 top-1 inline-flex items-center gap-0.5 rounded bg-amber-500 px-1 py-0.5 text-[8px] font-bold text-white"><AlertTriangle className="h-2.5 w-2.5" /> verzoek</span>}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Right: notepad */}
        <div className="lg:col-span-2">
          <div className="flex h-[600px] flex-col rounded-2xl border border-slate-200 bg-white shadow-sm" data-testid="notepad-panel">
            <div className="flex items-center justify-between border-b border-slate-100 px-5 py-3">
              <div className="font-display text-sm font-bold text-slate-900">Kladblok · gedeelde notities</div>
              <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-500">append-only</span>
            </div>
            <div className="flex-1 space-y-3 overflow-y-auto p-5">
              {notes.length === 0 && <div className="py-10 text-center text-sm text-slate-400">Nog geen notities. Voeg de eerste toe.</div>}
              {notes.map((n) => (
                <div key={n.id} data-testid={`note-${n.id}`} className={`rounded-xl border p-3 transition-colors ${newNoteIds.has(n.id) ? "border-amber-300 bg-amber-50 ring-2 ring-amber-200" : "border-slate-100 bg-slate-50"}`}>
                  <div className="mb-1 flex items-center gap-2">
                    <span className="text-xs font-semibold text-slate-900">{n.author}</span>
                    <span className="rounded bg-slate-200 px-1.5 py-0.5 text-[10px] font-medium text-slate-600">{ROLE_LABELS[n.author_role]}</span>
                    <span className="text-[11px] text-slate-400">{timeAgo(n.created_at)}</span>
                  </div>
                  <div className="whitespace-pre-wrap text-sm text-slate-700">{n.text}</div>
                </div>
              ))}
              <div ref={notesEnd} />
            </div>
            {!closed && (
              <form onSubmit={addNote} className="flex items-center gap-2 border-t border-slate-100 p-3">
                <input data-testid="note-input" value={note} onChange={(e) => setNote(e.target.value)}
                  onSelect={(e) => setSel((e.target.value.substring(e.target.selectionStart, e.target.selectionEnd) || "").trim())}
                  placeholder="Notitie… selecteer een woord om te zoeken, of zet het tussen (haakjes)"
                  className="flex-1 rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-blue-500" />
                {sel.length >= 2 && (
                  <button type="button" data-testid="kb-search-selection" onClick={() => searchKeyword(sel)}
                    className="inline-flex shrink-0 items-center gap-1 rounded-xl bg-yellow-400 px-3 py-2.5 text-xs font-bold text-slate-900 hover:bg-yellow-300">
                    <BookOpen className="h-3.5 w-3.5" /> Zoek "{sel.length > 14 ? sel.slice(0, 14) + "…" : sel}"
                  </button>
                )}
                <VoiceRecorder onTranscript={(t) => setNote((p) => (p ? `${p} ${t}` : t))} />
                <button data-testid="send-note-btn" type="submit" className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-600 text-white hover:bg-blue-500">
                  <Send className="h-4 w-4" />
                </button>
              </form>
            )}
          </div>
        </div>
      </div>

      {kbOpen && (
        <div className="fixed inset-0 z-[1500]" data-testid="kb-panel">
          <div className="absolute inset-0 bg-black/30" onClick={() => setKbOpen(false)} />
          <div className="absolute right-0 top-0 flex h-full w-full max-w-md flex-col bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
              <div className="flex items-center gap-2 font-display text-sm font-bold text-slate-900"><BookOpen className="h-4 w-4" /> Kennisbank · procedures</div>
              <button onClick={() => setKbOpen(false)} className="rounded-lg p-1 text-slate-400 hover:bg-slate-100"><X className="h-5 w-5" /></button>
            </div>
            <div className="border-b border-slate-100 p-3">
              <input data-testid="kb-panel-search" value={kbQuery} onChange={(e) => setKbQuery(e.target.value)} placeholder="Zoek in de kennisbank…" className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-blue-500" />
              <div className="mt-1 text-[11px] text-slate-400">{kbQuery.trim() ? "Zoekresultaten" : "Suggesties op basis van type, titel en kladblok"}</div>
            </div>
            <div className="flex-1 space-y-2 overflow-y-auto p-3">
              {kbResults.length === 0 ? <div className="py-8 text-center text-sm text-slate-400">Geen procedures gevonden.</div> :
                kbResults.map((a) => (
                  <div key={a.id} data-testid={`kb-result-${a.id}`} className="rounded-xl border border-slate-200">
                    <button onClick={() => setKbExpanded(kbExpanded === a.id ? null : a.id)} className="flex w-full items-start justify-between gap-2 p-3 text-left">
                      <div>
                        <div className="flex flex-wrap items-center gap-1.5 text-sm font-semibold text-slate-900">
                          {a.title}
                          {matchedKw[a.id]?.length > 0 && <span data-testid={`kb-via-${a.id}`} className="rounded bg-yellow-100 px-1.5 py-0.5 text-[10px] font-bold text-yellow-700">via "{matchedKw[a.id].join('", "')}"</span>}
                        </div>
                        {a.summary && <div className="text-xs text-slate-500">{a.summary}</div>}
                      </div>
                      <ChevronRight className={`h-4 w-4 shrink-0 text-slate-400 transition-transform ${kbExpanded === a.id ? "rotate-90" : ""}`} />
                    </button>
                    <div className="flex items-center justify-between gap-2 border-t border-slate-100 px-3 py-2">
                      <span className="text-[11px] text-slate-400">{pinnedIds.includes(a.id) ? "Zichtbaar in voertuig-app" : "Alleen meldkamer"}</span>
                      <button data-testid={`kb-pin-${a.id}`} onClick={() => togglePin(a.id)}
                        className={`inline-flex items-center gap-1 rounded-lg px-2.5 py-1 text-[11px] font-semibold transition-colors ${pinnedIds.includes(a.id) ? "bg-blue-600 text-white hover:bg-blue-500" : "border border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
                        <Pin className="h-3 w-3" /> {pinnedIds.includes(a.id) ? "In voertuig" : "Toon in voertuig"}
                      </button>
                    </div>
                    {kbExpanded === a.id && (
                      <div className="space-y-2 border-t border-slate-100 p-3">
                        {(a.sections || []).map((s, i) => (
                          <div key={i}>
                            <div className="text-[11px] font-bold uppercase tracking-wide text-blue-600">{s.heading}</div>
                            <ul className="mt-0.5 list-disc pl-4 text-sm text-slate-700">{(s.items || []).map((it, j) => <li key={j} className="whitespace-pre-wrap">{it}</li>)}</ul>
                          </div>
                        ))}
                        <div className="flex flex-wrap items-center gap-1 pt-1">
                          {(a.tags || []).map((t, i) => (
                            <span key={i} className="inline-flex items-center gap-1 rounded bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-600">
                              {t}
                              {!closed && <button data-testid={`kb-tag-del-${a.id}-${i}`} onClick={() => delKbTag(a.id, t)} className="text-slate-400 hover:text-red-600"><X className="h-2.5 w-2.5" /></button>}
                            </span>
                          ))}
                          {!closed && (
                            <input data-testid={`kb-tag-add-${a.id}`} placeholder="+ trefwoord"
                              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addKbTag(a.id, e.currentTarget.value); e.currentTarget.value = ""; } }}
                              className="w-24 rounded border border-dashed border-slate-300 px-1.5 py-0.5 text-[10px] outline-none focus:border-blue-500" />
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
            </div>
          </div>
        </div>
      )}

      {lightbox !== null && (
        <Lightbox photos={photos} startIndex={lightbox} onClose={() => setLightbox(null)}
          canModerate={canManage} onApproveDelete={approveDelete} onRejectDelete={rejectDelete} />
      )}
    </div>
  );
}
